import './Report.css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReportTickets } from '../../api/communication';

const STATUS_STEPS = ['open', 'in_progress', 'resolved'];

// DESIGN 1 -- "Status Timeline": the ticket's lifecycle (open -> in_progress -> resolved) is the
// headline feature, shown as a connected-dot stepper so the reporter can tell at a glance how
// far along their report is, instead of reading a plain status word. A colored header banner
// (color keyed to the current status) reinforces the same signal before you've even read it.
function Report({ reportId, onClose }) {
    const { t, i18n } = useTranslation();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        getReportTickets({ id: reportId }).then((data) => {
            if (cancelled) return;
            const found = Array.isArray(data) ? data[0] : null;
            if (found) setTicket(found); else setNotFound(true);
        }).catch((error) => {
            console.error('Error fetching report ticket:', error);
            if (!cancelled) setNotFound(true);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [reportId]);

    const formatDate = (value) => {
        if (!value) return '';
        return new Intl.DateTimeFormat(i18n.language, {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
        }).format(new Date(value));
    };

    const reporterName = ticket
        ? (`${ticket.first_name || ''} ${ticket.last_name || ''}`.trim() || t('manageReports.anonymousReporter'))
        : '';
    const currentStepIndex = ticket ? STATUS_STEPS.indexOf(ticket.status) : -1;
    // Mirrors Supervisor/ManageReports.jsx's own copy of this map -- maps a ticket's report_type
    // onto ModalReport.jsx's translated labels. Kept local to where it's used (not a top-level
    // constant) since this is the only place in this component that needs it.
    const typeLabelKey = ticket
        ? ({
              bug: 'modalReport.reportTypeBug',
              content: 'modalReport.reportTypeContent',
              account: 'modalReport.reportTypeAccount',
              privacy: 'modalReport.reportTypePrivacy',
              feature: 'modalReport.reportTypeFeature',
              content_request: 'modalReport.reportTypeContentRequest',
              other: 'modalReport.reportTypeOther',
          }[ticket.report_type] || 'modalReport.reportTypeOther')
        : '';

    return (
        <div className="report-overlay" onClick={onClose}>
            <div className="report-box" onClick={(e) => e.stopPropagation()}>
                <button className="report-close-x" onClick={onClose} aria-label={t('common.close')}>✕</button>

                {loading ? (
                    <p className="report-loading">{t('common.loading')}</p>
                ) : notFound || !ticket ? (
                    <p className="report-loading">{t('report.notFound')}</p>
                ) : (
                    <>
                        <div className={`report-header report-header-${ticket.status}`}>
                            <h2>{t('report.title')}</h2>
                        </div>

                        <div className="report-stepper">
                            {STATUS_STEPS.map((step, i) => (
                                <div
                                    key={step}
                                    className={`report-step ${i <= currentStepIndex ? 'done' : ''} ${i === currentStepIndex ? 'current' : ''}`}
                                >
                                    <span className="report-step-dot" />
                                    <span className="report-step-label">{t(`manageReports.status_${step}`)}</span>
                                    {i < STATUS_STEPS.length - 1 && <span className="report-step-line" />}
                                </div>
                            ))}
                        </div>

                        <div className="report-meta-grid">
                            <div className="report-meta-cell">
                                <span className="report-meta-label">{t('manageReports.reportedBy')}</span>
                                <span className="report-meta-value">{reporterName}</span>
                            </div>
                            <div className="report-meta-cell">
                                <span className="report-meta-label">{t('report.submittedLabel')}</span>
                                <span className="report-meta-value">{formatDate(ticket.created_at)}</span>
                            </div>
                        </div>

                        <div className="report-section">
                            <h3>{t('modalReport.descriptionLabel')}</h3>
                            <p className="report-description">{ticket.description}</p>
                        </div>

                        <div className="report-section report-type-section">
                            <h3>{t('modalReport.reportTypeLabel')}</h3>
                            <p className="report-description">{t(typeLabelKey)}</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Report;
